import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useShopFlagWithLoading } from "@/hooks/use-flag";
import { useViewerDateFnsLocale } from "@/lib/translator";
import useTranslation from "@babackapp/translations/use-translation";
import { DateRangePicker } from "@babackapp/ui/components/date-range-picker";
import { format, subMonths } from "date-fns";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { SizingConfigDialog } from "./components/sizing-config-dialog";
import { SizingDiagnosticTable } from "./components/sizing-diagnostic-table";
import { SizingExportButton } from "./components/sizing-export-button";
import { SizingImpactSection } from "./components/sizing-impact-section";

export default function SizingPage() {
  const t = useTranslation();
  const locale = useViewerDateFnsLocale();
  const { data: shop } = useCurrentShopSuspense();
  const { value: isSizingEnabled, loading: isSizingFlagLoading } =
    useShopFlagWithLoading("isSizingEnabled");
  const [dateRange, setDateRange] = useState(() => {
    const now = Date.now();
    return { from: subMonths(now, 3).getTime(), to: now };
  });

  if (isSizingFlagLoading) return null;
  if (!isSizingEnabled) {
    return <Navigate to={`/shops/${shop.id}`} replace />;
  }

  return (
    <div className="flex items-center justify-center">
      <div className="grid grid-cols-4 gap-4 w-full max-w-[1600px] lg:px-16 lg:py-6 px-6 py-4">
        {/* Header */}
        <div className="col-span-4 mb-4 space-y-2">
          <h2 className="text-2xl leading-6 font-semibold text-foreground">
            {t("sizing.pageTitle", {
              from: format(dateRange.from, "d MMMM yyyy", { locale }),
              to: format(dateRange.to, "d MMMM yyyy", { locale }),
            })}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground max-w-[600px]">
            {t("sizing.pageSubtitle")}
          </p>
        </div>

        {/* Toolbar */}
        <div className="col-span-4 flex items-center justify-end gap-2">
          <DateRangePicker
            className="w-auto"
            range={{ from: dateRange.from, to: dateRange.to }}
            onSelect={(range) => {
              if (range.from && range.to) {
                setDateRange({ from: range.from, to: range.to });
              }
            }}
            maxDate={new Date()}
          />
          <SizingExportButton
            fromTimestamp={dateRange.from}
            toTimestamp={dateRange.to}
          />
          <SizingConfigDialog />
        </div>

        {/* Impact section */}
        <SizingImpactSection
          fromTimestamp={dateRange.from}
          toTimestamp={dateRange.to}
        />

        {/* Diagnostic table */}
        <SizingDiagnosticTable
          fromTimestamp={dateRange.from}
          toTimestamp={dateRange.to}
        />
      </div>
    </div>
  );
}
